import React, { useLayoutEffect, useRef, useState } from "react";

import { css, keyframes } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";

import {
    estimatePathLength,
    generatePathD,
    getFillColor,
    type NodeCenterPct
} from "./spiderweb-utils";

import type {
    AnimationColor,
    ConnectorShape,
    FillColor,
    ResolvedConnector
} from "./spiderweb-types";
import type { AspectCorrection } from "./use-spiderweb-viewport-aspect";

type WebConnectorProps = {
    connector: ResolvedConnector;
    animated: boolean;
    delay: number;
    loadEntranceSolidDrawDurationSec?: number;
    loadEntranceDashedFadeDurationSec?: number;
    loadEntranceAnimatedShapeOpacityDurationSec?: number;
    loadEntranceAnimatedShapeAppearOffsetSec?: number;
    skipLoadEntrance?: boolean;
    color?: FillColor;
    strokeWidth?: number;
    isAnimated?: boolean;
    animationColor?: AnimationColor;
    viewBoxHeight?: number;
    fromPct?: NodeCenterPct;
    toPct?: NodeCenterPct;
    isFocused?: boolean;
    aspectCorrection?: AspectCorrection;
};

type AnimatedConnectorProps = {
    connectorShape: ConnectorShape;
    fromId: string;
    toId: string;
    animated: boolean;
    delay: number;
    skipLoadEntrance?: boolean;
    animationColor: AnimationColor;
    length: number;
    animatedShapeOpacityDurationSec: number;
    animatedShapeAppearOffsetSec: number;
    aspectCorrection?: AspectCorrection;
};
const focusColor = "hotpink";
const focusDasharray = "10 10";

const activeSelectionKeyframes = keyframes({
    "0%": {
        strokeDashoffset: 20
    },
    "100%": {
        strokeDashoffset: 0
    }
});

const activeConnectorCss = css({
    animationName: activeSelectionKeyframes,
    animationDuration: "0.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "steps(2)"
});

const DEFAULT_SOLID_DRAW_SEC = 1;
const DEFAULT_DASHED_FADE_SEC = 0.8;
const DEFAULT_ANIMATED_SHAPE_OPACITY_SEC = 0.6;
const DEFAULT_ANIMATED_SHAPE_APPEAR_OFFSET_SEC = 1;

export const AnimatedConnector = ({
    connectorShape,
    fromId,
    toId,
    animated,
    delay,
    skipLoadEntrance = false,
    animationColor,
    length,
    animatedShapeOpacityDurationSec,
    animatedShapeAppearOffsetSec,
    aspectCorrection
}: AnimatedConnectorProps) => {
    const appearDelay = delay + animatedShapeAppearOffsetSec;

    const style: React.CSSProperties = animated
        ? skipLoadEntrance
            ? { opacity: 1 }
            : {
                  opacity: 1,
                  transition: `opacity ${animatedShapeOpacityDurationSec}s ease ${appearDelay}s`
              }
        : {
              opacity: 0
          };

    const acX = aspectCorrection?.scaleX ?? 1;
    const acY = aspectCorrection?.scaleY ?? 1;
    const shapeTransform =
        acX !== 1 || acY !== 1 ? `scaleX(${acX}) scaleY(${acY})` : undefined;

    const duration = 10;
    const squarePath =
        "m-5-9h10c2.2 0 4 1.8 4 4v10c0 2.2-1.8 4-4 4h-10c-2.2 0-4-1.8-4-4v-10c0-2.2 1.8-4 4-4z";
    const circlePath = "m0 9c-5 0-9-4-9-9 0-5 4-9 9-9 5 0 9 4 9 9 0 5-4 9-9 9z";
    const pillPath =
        "m-19-9h38c5 0 9 4 9 9 0 5-4 9-9 9h-38c-5 0-9-4-9-9 0-5 4-9 9-9z";
    const trianglePath = "m-5-8l15 8-15 8z";
    const hexagonPath = "m7.8-4.6v9.2l-8 4.6-8-4.6v-9.2l8-4.6z";

    const colorMap = {
        lime: [
            wacToken("color.extended.lime500"),
            wacToken("color.extended.lime100"),
            wacToken("color.extended.lime300"),
            wacToken("color.extended.lime500"),
            wacToken("color.extended.lime200"),
            wacToken("color.extended.lime400"),
            wacToken("color.extended.lime200")
        ],
        purple: [
            wacToken("color.extended.purple500"),
            wacToken("color.extended.purple100"),
            wacToken("color.extended.purple300"),
            wacToken("color.extended.purple500"),
            wacToken("color.extended.purple200"),
            wacToken("color.extended.purple400"),
            wacToken("color.extended.purple200")
        ],
        blue: [
            wacToken("color.extended.blue500"),
            wacToken("color.extended.blue800"),
            wacToken("color.extended.blue600"),
            wacToken("color.extended.blue300"),
            wacToken("color.extended.blue700"),
            wacToken("color.extended.blue400"),
            wacToken("color.extended.blue600")
        ],
        yellow: [
            wacToken("color.extended.yellow500"),
            wacToken("color.extended.yellow100"),
            wacToken("color.extended.yellow300"),
            wacToken("color.extended.yellow500"),
            wacToken("color.extended.yellow200"),
            wacToken("color.extended.yellow400"),
            wacToken("color.extended.yellow200")
        ],
        neutral: [
            wacToken("color.neutral.light500"),
            wacToken("color.neutral.light300"),
            wacToken("color.neutral.light100"),
            wacToken("color.neutral.light400"),
            wacToken("color.neutral.light600"),
            wacToken("color.neutral.light200"),
            wacToken("color.neutral.light500")
        ]
    };
    const participatingShapes = [
        {
            shape: "circle",
            path: circlePath,
            delay: 0,
            fill: colorMap[animationColor][0]
        },
        {
            shape:
                connectorShape === "orthogonal" || shapeTransform !== undefined
                    ? "hexagon"
                    : "pill",
            path:
                connectorShape === "orthogonal" || shapeTransform !== undefined
                    ? hexagonPath
                    : pillPath,
            delay: 5,
            fill: colorMap[animationColor][1]
        },
        {
            shape: "triangle",
            path: trianglePath,
            delay: 2,
            fill: colorMap[animationColor][2]
        },
        {
            shape: "circle",
            path: circlePath,
            delay: 3,
            fill: colorMap[animationColor][3]
        },
        {
            shape: "triangle",
            path: trianglePath,
            delay: 9,
            fill: colorMap[animationColor][4]
        },
        {
            shape: "square",
            path: squarePath,
            delay: 1,
            fill: colorMap[animationColor][5]
        },
        {
            shape: "hexagon",
            path: hexagonPath,
            delay: 8,
            fill: colorMap[animationColor][6]
        }
    ];

    if (length < 400) {
        participatingShapes.splice(-2, 2);
    }

    return (
        <g style={style}>
            {participatingShapes.map((shape, index) => (
                <path
                    key={`anim-shape-${index}`}
                    className={`anim-shape-animated`}
                    fill={shape.fill}
                    d={shape.path}
                    style={
                        shape.shape !== "pill" && shape.shape !== "triangle"
                            ? { transform: shapeTransform }
                            : undefined
                    }
                >
                    <animateMotion
                        rotate={`${
                            shape.shape === "circle" ||
                            shape.shape === "hexagon" ||
                            shape.shape === "square"
                                ? "0"
                                : "auto"
                        }`}
                        dur={duration}
                        begin={`-${shape.delay}s;op.end-${shape.delay}s`}
                        repeatCount="indefinite"
                    >
                        <mpath href={`#connector-${fromId}-${toId}`} />
                    </animateMotion>
                </path>
            ))}
        </g>
    );
};

/* eslint-disable complexity -- dashed vs solid branches and optional animated overlay */
export const WebConnector = ({
    connector,
    animated,
    delay,
    loadEntranceSolidDrawDurationSec = DEFAULT_SOLID_DRAW_SEC,
    loadEntranceDashedFadeDurationSec = DEFAULT_DASHED_FADE_SEC,
    loadEntranceAnimatedShapeOpacityDurationSec = DEFAULT_ANIMATED_SHAPE_OPACITY_SEC,
    loadEntranceAnimatedShapeAppearOffsetSec = DEFAULT_ANIMATED_SHAPE_APPEAR_OFFSET_SEC,
    skipLoadEntrance = false,
    color = "neutral400",
    strokeWidth = 2,
    isAnimated = false,
    animationColor = "blue",
    viewBoxHeight,
    fromPct,
    toPct,
    isFocused = false,
    aspectCorrection
}: WebConnectorProps) => {
    const strokeColor = getFillColor(color);
    const focusWidth = strokeWidth + 10;
    const pathRef = useRef<SVGPathElement>(null);
    const lastMeasuredDRef = useRef<string | null>(null);
    const [pathLength, setPathLength] = useState<number | null>(null);

    const d = generatePathD(connector, viewBoxHeight, fromPct, toPct);
    const estimatedLength = estimatePathLength(
        connector,
        viewBoxHeight,
        fromPct,
        toPct
    );
    const length = Math.max(Number(pathLength ?? 0), estimatedLength) * 2;
    const isDashed = connector.connectorType === "dashed";

    useLayoutEffect(() => {
        const path = pathRef.current;
        if (!path || isDashed) return;
        lastMeasuredDRef.current = d;
        try {
            const total = path.getTotalLength();
            if (Number.isFinite(total) && total > 0) {
                setPathLength(total);
            }
        } catch {
            /* JSDOM (and some edge environments) omit or break SVG path length APIs */
        }
    }, [d, isDashed]);

    if (isDashed) {
        const style: React.CSSProperties = animated
            ? skipLoadEntrance
                ? { opacity: 1 }
                : {
                      opacity: 1,
                      transition: `opacity ${loadEntranceDashedFadeDurationSec}s ease ${delay}s`
                  }
            : {
                  opacity: 0
              };

        return (
            <g id={`connector-group-${connector.from.id}-${connector.to.id}`}>
                {isFocused ? (
                    <path
                        className="connector-focus-halo"
                        d={d}
                        fill="none"
                        stroke={focusColor}
                        strokeWidth={focusWidth}
                        strokeLinecap="butt"
                        strokeDasharray={focusDasharray}
                        vectorEffect="non-scaling-stroke"
                        style={style}
                    />
                ) : null}
                <path
                    id={`connector-${connector.from.id}-${connector.to.id}`}
                    d={d}
                    fill="none"
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    strokeDasharray="8 6"
                    strokeLinecap="round"
                    style={style}
                    vectorEffect="non-scaling-stroke"
                />
                {isAnimated && (
                    <AnimatedConnector
                        connectorShape={connector.connectorShape}
                        animationColor={animationColor}
                        fromId={connector.from.id}
                        toId={connector.to.id}
                        animated={animated}
                        delay={delay}
                        skipLoadEntrance={skipLoadEntrance}
                        length={length}
                        animatedShapeOpacityDurationSec={
                            loadEntranceAnimatedShapeOpacityDurationSec
                        }
                        animatedShapeAppearOffsetSec={
                            loadEntranceAnimatedShapeAppearOffsetSec
                        }
                        aspectCorrection={aspectCorrection}
                    />
                )}
            </g>
        );
    }

    const style: React.CSSProperties = animated
        ? skipLoadEntrance
            ? {
                  opacity: 1,
                  strokeDashoffset: 0
              }
            : {
                  opacity: 1,
                  strokeDashoffset: 0,
                  transition: `opacity 0.01s linear ${delay}s,
stroke-dashoffset ${loadEntranceSolidDrawDurationSec}s ease ${delay}s`
              }
        : {
              opacity: 0,
              strokeDashoffset: length
          };

    return (
        <g id={`connector-group-${connector.from.id}-${connector.to.id}`}>
            {isFocused ? (
                <path
                    css={activeConnectorCss}
                    className="connector-focus-halo"
                    d={d}
                    fill="none"
                    stroke={focusColor}
                    strokeWidth={focusWidth}
                    strokeLinecap="butt"
                    strokeDasharray={focusDasharray}
                    style={style}
                    vectorEffect="non-scaling-stroke"
                />
            ) : null}
            <path
                ref={pathRef}
                id={`connector-${connector.from.id}-${connector.to.id}`}
                d={d}
                fill="none"
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={length}
                strokeLinecap="round"
                style={style}
                vectorEffect="non-scaling-stroke"
            />
            {isAnimated && (
                <AnimatedConnector
                    connectorShape={connector.connectorShape}
                    animationColor={animationColor}
                    fromId={connector.from.id}
                    toId={connector.to.id}
                    animated={animated}
                    delay={delay}
                    skipLoadEntrance={skipLoadEntrance}
                    length={length}
                    animatedShapeOpacityDurationSec={
                        loadEntranceAnimatedShapeOpacityDurationSec
                    }
                    animatedShapeAppearOffsetSec={
                        loadEntranceAnimatedShapeAppearOffsetSec
                    }
                    aspectCorrection={aspectCorrection}
                />
            )}
        </g>
    );
};
/* eslint-enable complexity */
